import {
  hasFinanceReadonlyValue,
  resolvePrimaryBudgetCategoryName
} from '../components/ApprovalCenter/financeReadonlyUtils';

/** 报销 / 预付款详情抽屉 — 表单快照分组（与发起表单模块对应） */

export const GROUPED_FINANCE_SNAPSHOT_TYPES = new Set([
  'daily',
  'travel',
  'exp_prepayment',
  'exp_prepayment_writeoff',
  'prepayment_append'
]);

export const EXPENSE_CLAIM_SNAPSHOT_SECTIONS = [
  {
    title: '基本信息',
    keys: ['applicant_name', 'applicant_dept_name', 'expense_company_name', 'doc_type', 'urgency_label', 'related_approval_no']
  },
  {
    title: '预算与事由',
    keys: [
      'budget_subject_label', 'expense_occurrence_month',
      'budget_period_label', 'reason'
    ]
  },
  {
    title: '收款与税务',
    keys: [
      'payee_type_label', 'payee_name', 'bank_name', 'bank_branch_name',
      'bank_account_no', 'payment_date', 'payee_id_card_no', 'payee_tax_no'
    ]
  },
  {
    title: '报销明细',
    keys: ['expense_lines_summary']
  },
  {
    title: '金额汇总',
    keys: ['invoice_total', 'attachment_count']
  },
  {
    title: '附件与备注',
    keys: ['other_attachments', 'remark']
  }
];

export const EXP_PREPAYMENT_SNAPSHOT_SECTIONS = [
  {
    title: '基本信息',
    keys: ['applicant_name', 'applicant_dept_name', 'expense_company_name', 'doc_type', 'urgency_label']
  },
  {
    title: '预算与事由',
    keys: [
      'budget_org_name', 'budget_subject_label', 'expense_occurrence_month',
      'budget_period_label', 'reason', 'expected_invoice_date'
    ]
  },
  {
    title: '收款方',
    keys: [
      'payee_type_label', 'payee_name', 'bank_name', 'bank_branch_name',
      'bank_account_no', 'payee_id_card_no', 'payee_tax_no'
    ]
  },
  {
    title: '预付明细',
    keys: ['prepayment_lines_summary']
  },
  {
    title: '附件与备注',
    keys: ['no_invoice_commitment_files', 'other_attachments', 'remark']
  }
];

export const PREPAYMENT_APPEND_SNAPSHOT_SECTIONS = [
  {
    title: '关联单据',
    keys: ['prepayment_no', 'prepayment_writeoff_no', 'doc_type']
  },
  {
    title: '基本信息',
    keys: ['applicant_name', 'applicant_dept_name', 'expense_company_name', 'urgency_label']
  },
  {
    title: '预算与事由',
    keys: [
      'budget_subject_label', 'expense_occurrence_month', 'reason'
    ]
  },
  {
    title: '收款方',
    keys: ['payee_type_label', 'payee_name']
  },
  {
    title: '报销明细',
    keys: ['expense_lines_summary']
  },
  {
    title: '金额汇总',
    keys: ['invoice_total', 'actual_pay_total'],
    highlight: true
  },
  {
    title: '备注',
    keys: ['remark']
  }
];

export function getFinanceSnapshotSections(processType) {
  if (processType === 'exp_prepayment') return EXP_PREPAYMENT_SNAPSHOT_SECTIONS;
  if (processType === 'prepayment_append') return PREPAYMENT_APPEND_SNAPSHOT_SECTIONS;
  return EXPENSE_CLAIM_SNAPSHOT_SECTIONS;
}

export function getFinanceSnapshotSummaryMeta(processType) {
  if (processType === 'exp_prepayment') {
    return {
      amountKey: 'prepay_total',
      amountLabel: '预付总额',
      uppercaseKey: 'prepay_total_uppercase',
      uppercaseLabel: '金额大写'
    };
  }
  return {
    amountKey: 'actual_pay_total',
    amountLabel: '实付金额',
    uppercaseKey: 'amount_uppercase',
    uppercaseLabel: '金额大写'
  };
}

function resolveSnapshotPayeeTypeKey(formData = {}) {
  const type = String(formData.payee_type || '').trim();
  if (type) return type;
  const label = String(formData.payee_type_label || '').trim();
  if (label.includes('员工')) return 'employee';
  if (label.includes('外部')) return 'external_person';
  if (label.includes('供应商')) return 'supplier';
  return '';
}

function shouldIncludeSnapshotItem(item, formData = {}, processType) {
  if (!item?.key) return false;
  if (item.key === 'budget_org_name' || item.key === 'subject') return false;
  if (item.key === 'expected_invoice_date' && processType !== 'exp_prepayment') return false;

  const payeeType = resolveSnapshotPayeeTypeKey(formData);
  if (payeeType === 'employee' && ['bank_branch_name', 'payee_id_card_no', 'payee_tax_no'].includes(item.key)) {
    return false;
  }
  if (payeeType === 'external_person' && item.key === 'payee_tax_no') return false;
  if (payeeType === 'supplier' && item.key === 'payee_id_card_no') return false;
  if (['bank_branch_name', 'payee_id_card_no', 'payee_tax_no', 'expected_invoice_date'].includes(item.key)) {
    return hasFinanceReadonlyValue(item.value);
  }
  return true;
}

function normalizeFinanceSnapshotItem(item, formData = {}) {
  if (item.key === 'budget_subject_label') {
    const value = resolvePrimaryBudgetCategoryName(formData);
    return {
      ...item,
      label: '预算一级科目',
      value: value !== '—' ? value : item.value
    };
  }
  return item;
}

export function groupFinanceSnapshotItems(items, processType, formData = {}) {
  const sections = getFinanceSnapshotSections(processType);
  const byKey = new Map();
  (items || []).forEach((item) => {
    if (item?.key) byKey.set(item.key, item);
  });

  const used = new Set();
  const grouped = sections.map((section) => {
    const sectionItems = section.keys
      .map((key) => byKey.get(key))
      .filter(Boolean)
      .filter((item) => shouldIncludeSnapshotItem(item, formData, processType))
      .map((item) => {
        used.add(item.key);
        return normalizeFinanceSnapshotItem(item, formData);
      });
    return { ...section, items: sectionItems };
  }).filter((section) => section.items.length > 0);

  const rest = (items || [])
    .filter((item) => item?.key && !used.has(item.key))
    .filter((item) => shouldIncludeSnapshotItem(item, formData, processType))
    .map((item) => normalizeFinanceSnapshotItem(item, formData));
  if (rest.length) {
    grouped.push({ title: '其他', items: rest });
  }
  return grouped;
}
